import React, { useContext, useEffect, useLayoutEffect, useState } from "react";
import { useLocation } from "@reach/router";
import { useRadioidByNameseo } from "../../hooks/useApi";
import useLanguage from "../../hooks/useLanguage";
import { getEntryIntent, getRadioDestinationPath } from "./entryIntentStorage";
import EntryLanguageFooter from "../../components/EntryLanguageFooter";
import SonicboxLogo from "../../images/sonicbox-logo.png";
import "./entryBranding.scss";

import EntryBrandingContext from "./entryBrandingContext";

// The controller knows when access has finished; the shared layout survives
// route changes to password/email screens and their reloads.
export function useEntryBrandingRegistration({ pathname, nameseo, active }) {
  const { register } = useContext(EntryBrandingContext);
  useLayoutEffect(() => register({ pathname, nameseo, active }), [register, pathname, nameseo, active]);
}

export function EntryBrandingProvider({ children }) {
  const { pathname } = useLocation();
  const [stage, setStage] = useState(null);
  const [register] = useState(() => (next) => {
    setStage(next);
    return () => setStage(current => current === next ? null : current);
  });
  const match = pathname.match(/^\/(sonicbox|task)\/([^/?#]+)/);
  const redeem = pathname.startsWith("/redeem/");
  const auth = ["/login", "/verify", "/loginemail", "/register"].includes(pathname);
  const intent = auth ? getEntryIntent() : null;
  const destination = auth ? getRadioDestinationPath() : null;
  const authNameseo = intent?.target?.nameseo || destination?.match(/^\/sonicbox\/([^/?#]+)/)?.[1];
  const currentStage = stage?.pathname === pathname ? stage : null;
  const active = currentStage ? currentStage.active : !!(match || redeem || (auth && (authNameseo || intent)));
  // Never infer an unresolved QR's identity from the previous destination.
  const nameseo = active ? (currentStage ? currentStage.nameseo : match?.[2] || (auth ? authNameseo : null)) : null;
  const radio = useRadioidByNameseo(nameseo);
  const data = radio.data;
  const [loadedBackground, setLoadedBackground] = useState(null);
  const background = data?.radiobg;
  useEffect(() => {
    if (!active || !background) return;
    let cancelled = false;
    const image = new Image();
    image.onload = () => { if (!cancelled) setLoadedBackground(background); };
    image.src = background;
    return () => { cancelled = true; image.onload = null; };
  }, [active, background]);
  const visibleBackground = active && background && loadedBackground === background ? background : null;
  const context = { active, data, register };
  return (
    <EntryBrandingContext.Provider value={context}>
      <div className={active ? "entry-access entry-access--active" : "entry-access"}>
        {active && <>
          <div className="entry-branding__background" aria-hidden="true" style={{ "--entry-branding-shade": "0.6", backgroundImage: visibleBackground ? `linear-gradient(rgba(1,18,27,var(--entry-branding-shade)),rgba(1,18,27,var(--entry-branding-shade))), url(${JSON.stringify(visibleBackground)})` : undefined }} />
          <header className="entry-branding__platform"><img src={SonicboxLogo} alt="Sonicbox" /></header>
        </>}
        {children}
      </div>
    </EntryBrandingContext.Provider>
  );
}

export function EntryBrandingIdentity() {
  const { active, data } = useContext(EntryBrandingContext);
  const { mayorLanguage } = useLanguage();
  const [failedLogo, setFailedLogo] = useState(null);
  if (!active) return null;
  return <section className="entry-branding__identity">
    {data?.radioimage && failedLogo !== data.radioimage && <img src={data.radioimage} alt={data.name || ""} onError={() => setFailedLogo(data.radioimage)} />}
    {data?.name && <><p className="entry-branding__eyebrow">{mayorLanguage === "en" ? "YOU ARE AT" : "ESTÁS EN"}</p><h1>{data.name}</h1></>}
  </section>;
}

export function EntryBrandingLanguage() {
  const { active } = useContext(EntryBrandingContext);
  return active ? <div className="entry-branding__language"><EntryLanguageFooter entryShell /></div> : null;
}
